import {
	isCommercialSuretyBondType,
	type SuretyAccountId,
	type SuretyQuoteId,
} from "@inrev/common";
import type { BondRequestFlowAnalytics } from "@portal/hooks/useBondRequestFlowAnalytics";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { type PendingOutcomeEntry } from "@portal/providers/PendingBondRequestOutcomeProvider";
import { type FC, useCallback, useEffect } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AdminBondRequestSubmissionAdapterProvider } from "./AdminSubmissionAdapterProvider";
import { type SubmissionFormData } from "./bondRequestSubmissionFormSchema";
import { useBondRequestSubmissionFlow } from "./bondRequestSubmissionModalState";
import { AdminAccountAndAgencyStep } from "./steps/AdminAccountAndAgencyStep";
import { OutcomeSubscriber } from "./steps/BondRequestSubmissionSubscribers";
import { CommercialInfoStep } from "./steps/CommercialInfoStep";
import { type BondRequestSubmitErrorName, FilesStep } from "./steps/FilesStep";
import { OutcomeStep } from "./steps/OutcomeStep";
import { PrincipalAndTypeStep } from "./steps/PrincipalAndTypeStep";
import { SummaryStep } from "./steps/SummaryStep";
import { WaitingStep } from "./steps/WaitingStep";

type BondRequestSubmissionFlowBodyProps = {
	isAdmin?: boolean;
	accountId?: SuretyAccountId;
	onClose: () => void;
	analytics: BondRequestFlowAnalytics;
	formMethods: UseFormReturn<SubmissionFormData>;
	onSubmissionStarted: () => void;
	onSubmissionFailed: () => void;
	onWaitingStateChange?: (entry: PendingOutcomeEntry | undefined) => void;
};

export const BondRequestSubmissionFlowBody: FC<BondRequestSubmissionFlowBodyProps> = ({
	isAdmin,
	accountId,
	onClose,
	analytics,
	formMethods,
	onSubmissionStarted,
	onSubmissionFailed,
	onWaitingStateChange,
}) => {
	const startAtAccountSelection = !!isAdmin;
	const {
		state,
		goToPrincipalAndType,
		goToFiles,
		goToCommercialInfo,
		goToCommercialSummary,
		goToSubmitting,
		goToResolving,
		goToWaiting,
		goToOutcome,
		goToSubmitError,
		reset,
	} = useBondRequestSubmissionFlow(startAtAccountSelection);
	const navigate = useNavigate();
	const { showErrorFlag } = useFlags();

	useEffect(() => {
		if (state.step !== "waiting") {
			onWaitingStateChange?.(undefined);
			return;
		}
		if (!accountId) return;
		onWaitingStateChange?.({
			quoteId: state.quoteId,
			isAdmin: !!isAdmin,
			accountId,
		});
	}, [state, isAdmin, accountId, onWaitingStateChange]);

	const { onEntered, onCreated, onOutcomeShown, onWaitingStarted, onWaitResolved, onFailed } =
		analytics;

	const waitingQuoteId = state.step === "waiting" ? state.quoteId : undefined;
	useEffect(() => {
		if (waitingQuoteId) onWaitingStarted(waitingQuoteId);
	}, [waitingQuoteId, onWaitingStarted]);

	useEffect(() => {
		if (accountId) onEntered(accountId);
	}, [accountId, onEntered]);

	const handlePrincipalAndTypeNext = useCallback(() => {
		const bondType = formMethods.getValues("bondType");
		if (isCommercialSuretyBondType(bondType)) {
			goToCommercialInfo();
			return;
		}
		goToFiles();
	}, [formMethods, goToCommercialInfo, goToFiles]);

	const handleSubmitting = useCallback(() => {
		goToSubmitting();
		onSubmissionStarted();
	}, [goToSubmitting, onSubmissionStarted]);

	const handleSubmitError = useCallback(
		(errorName: BondRequestSubmitErrorName) => {
			goToSubmitError();
			onSubmissionFailed();
			if (accountId) onFailed(accountId, errorName);
			showErrorFlag({
				heading: "Submission Failed",
				message: "We couldn't submit this bond request. Please try again.",
			});
		},
		[goToSubmitError, onSubmissionFailed, onFailed, accountId, showErrorFlag],
	);

	const handleSubmitted = useCallback(
		(createdQuoteId: SuretyQuoteId) => {
			goToResolving(createdQuoteId);
			if (accountId) onCreated(accountId, createdQuoteId);
		},
		[goToResolving, onCreated, accountId],
	);

	const quoteId = "quoteId" in state ? state.quoteId : undefined;
	const handleOutcome = useCallback(
		(outcome: "approved" | "review") => {
			goToOutcome(outcome);
			// No-ops unless the flow actually sat on the waiting step -- a request
			// that resolves straight out of `resolving` was never a wait at all.
			onWaitResolved("waited");
			if (accountId && quoteId) {
				onOutcomeShown(accountId, quoteId, outcome);
			}
		},
		[goToOutcome, onOutcomeShown, onWaitResolved, quoteId, accountId],
	);

	const adapterSteps = (
		<>
			{(state.step === "files" || state.step === "submitting" || state.step === "resolving") &&
				accountId && (
					<FilesStep
						accountId={accountId}
						isSubmitting={state.step === "submitting" || state.step === "resolving"}
						onBack={goToPrincipalAndType}
						onSubmitting={handleSubmitting}
						onSubmitted={handleSubmitted}
						onSubmitError={handleSubmitError}
					/>
				)}
			{(state.step === "resolving" || state.step === "waiting") && (
				<OutcomeSubscriber
					quoteId={state.quoteId}
					onOutcome={handleOutcome}
					onProcessing={goToWaiting}
				/>
			)}
		</>
	);

	const submissionSteps = (
		<>
			{state.step === "principalAndType" && accountId && (
				<PrincipalAndTypeStep
					accountId={accountId}
					isAdmin={isAdmin}
					onCancel={onClose}
					onBack={startAtAccountSelection ? reset : undefined}
					onNext={handlePrincipalAndTypeNext}
				/>
			)}
			{state.step === "commercialInfo" && (
				<CommercialInfoStep onBack={goToPrincipalAndType} onNext={goToCommercialSummary} />
			)}
			{state.step === "commercialSummary" && accountId && (
				<SummaryStep
					onBack={goToCommercialInfo}
					onSubmit={() => {
						// TODO (DEV-3411): commercial submission
					}}
				/>
			)}
			{adapterSteps}
		</>
	);

	return (
		<FormProvider {...formMethods}>
			<div data-testid="submission-modal">
				{startAtAccountSelection && state.step === "accountAndAgency" && (
					<AdminAccountAndAgencyStep onCancel={onClose} onNext={goToPrincipalAndType} />
				)}
				{isAdmin && accountId ? (
					<AdminBondRequestSubmissionAdapterProvider accountId={accountId}>
						{submissionSteps}
					</AdminBondRequestSubmissionAdapterProvider>
				) : (
					submissionSteps
				)}
				{state.step === "waiting" && <WaitingStep onClose={onClose} />}
				{state.step === "outcome" && (
					<OutcomeStep
						outcome={state.outcome}
						onViewDetails={() => {
							if (!accountId) {
								throw new Error("accountId required to view bond request details");
							}
							navigate(`/accounts/${accountId}/requests/${state.quoteId}`);
						}}
					/>
				)}
			</div>
		</FormProvider>
	);
};
